/**
 * Date-of-death-aware HMRC authority resolver — citation source of truth.
 * =======================================================================
 *
 * Given a date of death, returns the legislation / HMRC manual / form / GOV.UK
 * reference that was IN FORCE at that date for each feature area. This is what
 * makes "advice linked to the correct HMRC guidance for the date of death"
 * real on the citation axis (taxBands.ts is the equivalent on the numeric axis).
 *
 * Pure data + lookups, no I/O — importable from both the server (audit-trail
 * threading) and the client (report components), replacing the hardcoded
 * legislation/manual strings that were scattered across components.
 *
 * COMPLIANCE: server/tests/hmrc/rule-register.json remains the canonical ledger.
 * rule-matrix.test.ts asserts every register rule maps to a feature here that is
 * in force in the current era, so the ledger and this runtime source can't drift.
 *
 * DATE GATING (from taxBands.KEY_DATES) — the cases that actually change with the
 * date of death:
 *   - RNRB / TRNRB / RNRB taper: only on/after 6 Apr 2017.
 *   - Reduced 36% charity rate: only on/after 6 Apr 2012.
 *   - Transferable NRB: only on/after 9 Oct 2007.
 *   - APR/BPR £2.5m combined cap (distinct statute): only on/after 6 Apr 2026.
 *   - Excepted-estate regime: simplified rules differ pre/post 1 Jan 2022.
 */

export interface Authority {
    label: string;             // human-readable title for display
    legislation?: string;      // e.g. "IHTA 1984 s8A"
    manual?: string;           // e.g. "IHTM43001" (HMRC Inheritance Tax Manual)
    form?: string;             // e.g. "IHT402"
    url: string;               // GOV.UK / HMRC link
    effectiveFrom: string;     // ISO date, inclusive
    effectiveTo: string | null; // ISO date, inclusive; null = open-ended
}

export type FeatureArea =
    | 'NRB'
    | 'TNRB'
    | 'RNRB'
    | 'TRNRB'
    | 'RNRB_TAPER'
    | 'GIFTS_7YR'
    | 'ANNUAL_EXEMPTION'
    | 'NORMAL_EXPENDITURE'
    | 'TAPER_RELIEF'
    | 'SPOUSE_EXEMPTION'
    | 'EXCEPTED_ESTATE'
    | 'RATE_STANDARD'
    | 'RATE_CHARITABLE'
    | 'BPR'
    | 'APR'
    | 'APR_BPR_COMBINED_CAP'
    | 'TRUST_GWR'
    | 'FORM_IHT400'
    | 'FORM_IHT402'
    | 'FORM_IHT435'
    | 'FORM_IHT436';

const IHT_START = '1986-03-18';
const RNRB_FROM = '2017-04-06';
const TNRB_FROM = '2007-10-09';
const CHARITY_36_FROM = '2012-04-06';
const REFORM_2026 = '2026-04-06';
const EXCEPTED_POST_2022 = '2022-01-01';

/**
 * Effective-dated authority versions per feature area. Within an area, versions
 * are ordered so the lookup matches the entry whose window contains the date.
 * Transcribed from rule-register.json's sourceReference, split into structured
 * legislation / manual / form fields.
 */
const AUTHORITIES: Record<FeatureArea, Authority[]> = {
    NRB: [{
        label: 'Nil rate band',
        legislation: 'IHTA 1984 s7, Schedule 1',
        manual: 'IHTM05031',
        url: 'https://www.gov.uk/inheritance-tax',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    TNRB: [{
        label: 'Transferable nil rate band',
        legislation: 'IHTA 1984 s8A',
        manual: 'IHTM43001',
        form: 'IHT402',
        url: 'https://www.gov.uk/guidance/inheritance-tax-transfer-of-threshold',
        effectiveFrom: TNRB_FROM, effectiveTo: null,
    }],
    RNRB: [{
        label: 'Residence nil rate band',
        legislation: 'IHTA 1984 s8D–8M',
        manual: 'IHTM46001',
        form: 'IHT435',
        url: 'https://www.gov.uk/guidance/inheritance-tax-residence-nil-rate-band',
        effectiveFrom: RNRB_FROM, effectiveTo: null,
    }],
    TRNRB: [{
        label: 'Transferable residence nil rate band',
        legislation: 'IHTA 1984 s8FE',
        manual: 'IHTM46030',
        form: 'IHT436',
        url: 'https://www.gov.uk/guidance/inheritance-tax-residence-nil-rate-band',
        effectiveFrom: RNRB_FROM, effectiveTo: null,
    }],
    RNRB_TAPER: [{
        label: 'Residence nil rate band taper',
        legislation: 'IHTA 1984 s8D(5)',
        manual: 'IHTM46022',
        url: 'https://www.gov.uk/guidance/inheritance-tax-residence-nil-rate-band',
        effectiveFrom: RNRB_FROM, effectiveTo: null,
    }],
    GIFTS_7YR: [{
        label: 'Gifts within 7 years of death',
        legislation: 'IHTA 1984 s3A',
        manual: 'IHTM14000',
        form: 'IHT403',
        url: 'https://www.gov.uk/inheritance-tax/gifts',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    ANNUAL_EXEMPTION: [{
        label: 'Annual exemption',
        legislation: 'IHTA 1984 s19',
        manual: 'IHTM14141',
        url: 'https://www.gov.uk/inheritance-tax/gifts',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    NORMAL_EXPENDITURE: [{
        label: 'Normal expenditure out of income',
        legislation: 'IHTA 1984 s21',
        manual: 'IHTM14231',
        url: 'https://www.gov.uk/hmrc-internal-manuals/inheritance-tax-manual/ihtm14231',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    TAPER_RELIEF: [{
        label: 'Taper relief',
        legislation: 'IHTA 1984 s7(4)',
        manual: 'IHTM14611',
        url: 'https://www.gov.uk/inheritance-tax/gifts',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    SPOUSE_EXEMPTION: [{
        label: 'Spouse / civil partner exemption',
        legislation: 'IHTA 1984 s18',
        manual: 'IHTM11031',
        url: 'https://www.gov.uk/inheritance-tax/passing-on-home',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    EXCEPTED_ESTATE: [
        {
            // Pre-1-Jan-2022 excepted-estate conditions (earlier IHT205 regime).
            label: 'Excepted estate (pre-2022 conditions)',
            manual: 'IHTM06012',
            legislation: 'SI 2004/2543',
            url: 'https://www.gov.uk/valuing-estate-of-deceased/check-type-of-estate',
            effectiveFrom: '2004-11-01', effectiveTo: '2021-12-31',
        },
        {
            // Post-1-Jan-2022 simplified reporting.
            label: 'Excepted estate (post-2022 simplified reporting)',
            manual: 'IHTM06013',
            legislation: 'SI 2004/2543 as amended',
            url: 'https://www.gov.uk/government/publications/changes-to-the-reporting-requirements-for-inheritance-tax',
            effectiveFrom: EXCEPTED_POST_2022, effectiveTo: null,
        },
    ],
    RATE_STANDARD: [{
        label: 'Standard 40% rate',
        legislation: 'IHTA 1984 s7, Schedule 1',
        url: 'https://www.gov.uk/inheritance-tax',
        effectiveFrom: '1988-03-15', effectiveTo: null,
    }],
    RATE_CHARITABLE: [{
        // Reduced 36% rate did not exist before 6 Apr 2012.
        label: 'Reduced 36% charity rate',
        legislation: 'IHTA 1984 s7(2), Schedule 1A',
        manual: 'IHTM45001',
        url: 'https://www.gov.uk/inheritance-tax/giving-to-charity-to-reduce-an-inheritance-tax-bill',
        effectiveFrom: CHARITY_36_FROM, effectiveTo: null,
    }],
    BPR: [{
        label: 'Business Property Relief',
        legislation: 'IHTA 1984 s104–114',
        manual: 'IHTM18000',
        url: 'https://www.gov.uk/business-relief-inheritance-tax',
        effectiveFrom: '1992-03-10', effectiveTo: null,
    }],
    APR: [{
        label: 'Agricultural Property Relief',
        legislation: 'IHTA 1984 s115–124C',
        manual: 'IHTM24000',
        url: 'https://www.gov.uk/agricultural-relief-inheritance-tax',
        effectiveFrom: '1992-09-01', effectiveTo: null,
    }],
    APR_BPR_COMBINED_CAP: [{
        // £2.5m combined 100% APR/BPR allowance; 50% relief above. New from 6 Apr 2026.
        label: 'APR/BPR combined £2.5m allowance',
        legislation: 'Finance Act 2025 (APR/BPR reform)',
        manual: 'IHTM18000',
        url: 'https://www.gov.uk/business-relief-inheritance-tax',
        effectiveFrom: REFORM_2026, effectiveTo: null,
    }],
    TRUST_GWR: [{
        label: 'Trusts & gifts with reservation of benefit',
        legislation: 'IHTA 1984 s49–57; Finance Act 1986 s102',
        manual: 'IHTM04000',
        url: 'https://www.gov.uk/hmrc-internal-manuals/inheritance-tax-manual/ihtm04000',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    FORM_IHT400: [{
        label: 'IHT400 — full Inheritance Tax account',
        form: 'IHT400',
        manual: 'IHTM06011',
        url: 'https://www.gov.uk/government/publications/inheritance-tax-inheritance-tax-account-iht400',
        effectiveFrom: IHT_START, effectiveTo: null,
    }],
    FORM_IHT402: [{
        label: 'IHT402 — transfer unused nil rate band',
        form: 'IHT402',
        legislation: 'IHTA 1984 s8A',
        url: 'https://www.gov.uk/government/publications/inheritance-tax-claim-to-transfer-unused-nil-rate-band-iht402',
        effectiveFrom: TNRB_FROM, effectiveTo: null,
    }],
    FORM_IHT435: [{
        label: 'IHT435 — residence nil rate band claim',
        form: 'IHT435',
        legislation: 'IHTA 1984 s8D',
        url: 'https://www.gov.uk/government/publications/inheritance-tax-residence-nil-rate-band-iht435',
        effectiveFrom: RNRB_FROM, effectiveTo: null,
    }],
    FORM_IHT436: [{
        label: 'IHT436 — transfer unused residence nil rate band',
        form: 'IHT436',
        legislation: 'IHTA 1984 s8FE',
        url: 'https://www.gov.uk/government/publications/inheritance-tax-claim-to-transfer-unused-residence-nil-rate-band-iht436',
        effectiveFrom: RNRB_FROM, effectiveTo: null,
    }],
};

/**
 * Resolve the authority in force at a given date of death for a feature area.
 * Returns null when the feature did not yet exist at that date (e.g. RNRB before
 * 6 Apr 2017) — callers should then suppress the citation entirely.
 *
 * @param dateOfDeath - ISO date (YYYY-MM-DD). Falsy ⇒ treated as "today/current".
 */
export function getAuthority(area: FeatureArea, dateOfDeath?: string | null): Authority | null {
    const dod = dateOfDeath && dateOfDeath.length >= 10 ? dateOfDeath : '9999-12-31';
    const versions = AUTHORITIES[area];
    if (!versions) return null;
    return (
        versions.find(
            (a) => a.effectiveFrom <= dod && (a.effectiveTo === null || dod <= a.effectiveTo),
        ) ?? null
    );
}

/** All authorities in force at a date of death, keyed by feature area (nulls omitted). */
export function getAuthorities(dateOfDeath?: string | null): Partial<Record<FeatureArea, Authority>> {
    const out: Partial<Record<FeatureArea, Authority>> = {};
    for (const area of Object.keys(AUTHORITIES) as FeatureArea[]) {
        const a = getAuthority(area, dateOfDeath);
        if (a) out[area] = a;
    }
    return out;
}

/**
 * Compact citation shape for the audit trail / API payloads — only the fields
 * the report renders, plus the effective window so the linkage is auditable.
 */
export interface CitationRef {
    label: string;
    legislation?: string;
    manual?: string;
    form?: string;
    url: string;
    effectiveFrom: string;
    effectiveTo: string | null;
}

export function toCitation(a: Authority): CitationRef {
    return {
        label: a.label,
        legislation: a.legislation,
        manual: a.manual,
        form: a.form,
        url: a.url,
        effectiveFrom: a.effectiveFrom,
        effectiveTo: a.effectiveTo,
    };
}

/** Convenience: resolve a feature area straight to a CitationRef (or null). */
export function citationAt(area: FeatureArea, dateOfDeath?: string | null): CitationRef | null {
    const a = getAuthority(area, dateOfDeath);
    return a ? toCitation(a) : null;
}

/**
 * Maps rule-register internalRuleId → FeatureArea, for the compliance test that
 * keeps the ledger and this runtime source aligned. Authorities-only entries
 * (e.g. APR_BPR_COMBINED_CAP, EXCEPTED_ESTATE pre-2022) need no register row.
 */
export const RULE_ID_TO_FEATURE: Record<string, FeatureArea> = {
    NRB_BASE: 'NRB',
    TNRB: 'TNRB',
    RNRB_BASE: 'RNRB',
    TRNRB: 'TRNRB',
    RNRB_TAPER: 'RNRB_TAPER',
    GIFTS_7_YEAR: 'GIFTS_7YR',
    ANNUAL_GIFT_EXEMPTION: 'ANNUAL_EXEMPTION',
    GIFTS_NORMAL_EXPENDITURE: 'NORMAL_EXPENDITURE',
    TAPER_RELIEF: 'TAPER_RELIEF',
    SPOUSE_EXEMPTION: 'SPOUSE_EXEMPTION',
    EXCEPTED_ESTATE_POST_2022: 'EXCEPTED_ESTATE',
    IHT_RATE_STANDARD: 'RATE_STANDARD',
    IHT_RATE_CHARITABLE: 'RATE_CHARITABLE',
    BPR_FULL: 'BPR',
    APR_FULL: 'APR',
    TRUST_GWR_SCREENING: 'TRUST_GWR',
    FORM_IHT400: 'FORM_IHT400',
    FORM_IHT402: 'FORM_IHT402',
    FORM_IHT435: 'FORM_IHT435',
    FORM_IHT436: 'FORM_IHT436',
};
